/**
 * Users' Manual Page
 * Comprehensive guide for using the Traditional Marriage & Divorce Certificate System
 */

import { useState } from "react";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useEffect } from "react";

type Section =
  | "overview"
  | "login"
  | "dashboard"
  | "apply"
  | "approve"
  | "print"
  | "finalize"
  | "users"
  | "roles"
  | "workflows"
  | "settings"
  | "masterdata"
  | "auditlogs"
  | "profile";

const SECTIONS: { id: Section; label: string; icon: string }[] = [
  { id: "overview", label: "System Overview", icon: "📖" },
  { id: "login", label: "Logging In", icon: "🔐" },
  { id: "dashboard", label: "Dashboard", icon: "🏠" },
  { id: "apply", label: "Apply for Certificate", icon: "📝" },
  { id: "approve", label: "Approving Applications", icon: "✅" },
  { id: "print", label: "Printing Certificates", icon: "🖨️" },
  { id: "finalize", label: "Finalizing Certificates", icon: "📋" },
  { id: "users", label: "Managing Users", icon: "👥" },
  { id: "roles", label: "Roles & Permissions", icon: "🔒" },
  { id: "workflows", label: "Workflows", icon: "🔄" },
  { id: "settings", label: "Settings", icon: "⚙️" },
  { id: "masterdata", label: "Master Data", icon: "🗂️" },
  { id: "auditlogs", label: "Audit Logs", icon: "📊" },
  { id: "profile", label: "Your Profile", icon: "👤" },
];

const UsersManual = () => {
  const { setPageTitle } = usePageTitle();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<Section>("overview");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 881px)").matches;
    setSidebarOpen(isDesktop);
    const handleResize = () =>
      setSidebarOpen(window.matchMedia("(min-width: 881px)").matches);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setPageTitle("Users' Manual", "How to use the TWDC system");
  }, [setPageTitle]);

  const scrollTo = (id: Section) => {
    setActiveSection(id);
    document
      .getElementById(`section-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className={`dashboard-body ${sidebarOpen ? "sidebar-open" : "sidebar-closed"}`}
    >
      <Topbar
        onMenuToggle={() => setSidebarOpen((p) => !p)}
        sidebarOpen={sidebarOpen}
      />
      <Sidebar isOpen={sidebarOpen} />
      <main className="main-content">
        <div className="manual-layout">
          {/* ── Table of Contents ── */}
          <nav className="manual-toc">
            <h3 className="manual-toc-title">Table of Contents</h3>
            <ul>
              {SECTIONS.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`manual-toc-link ${activeSection === s.id ? "active" : ""}`}
                    onClick={() => scrollTo(s.id)}
                  >
                    <span>{s.icon}</span> {s.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Content ── */}
          <article className="manual-content">
            {/* ── Overview ── */}
            <section id="section-overview" className="manual-section">
              <h1 className="manual-h1">📖 Users' Manual</h1>
              <p className="manual-subtitle">
                Traditional Marriage &amp; Divorce Certificate System (TWDC) —
                Step-by-step guide
              </p>

              <h2 className="manual-h2">System Overview</h2>
              <p>
                The{" "}
                <strong>
                  Traditional Marriage &amp; Divorce Certificate System (TWDC)
                </strong>{" "}
                is an online platform used by the Ministry of Internal Affairs
                to manage the application, review, approval, and printing of
                traditional marriage and divorce certificates in Liberia.
              </p>
              <p>
                This manual will guide you through every part of the system,
                step by step. You do not need any previous computer experience
                to follow along — every instruction is written in plain
                language.
              </p>

              <div className="manual-tip">
                <strong>💡 Tip:</strong> Use the <em>Table of Contents</em> on
                the left to jump directly to the topic you need.
              </div>

              <h3 className="manual-h3">What You Can Do in This System</h3>
              <table className="manual-table">
                <thead>
                  <tr>
                    <th>Module</th>
                    <th>What it does</th>
                    <th>Who uses it</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Apply</td>
                    <td>Submit a new marriage certificate application</td>
                    <td>Registry Officers</td>
                  </tr>
                  <tr>
                    <td>Approve</td>
                    <td>Review and approve submitted applications</td>
                    <td>Supervisors / Officers</td>
                  </tr>
                  <tr>
                    <td>Print</td>
                    <td>Print the approved certificate</td>
                    <td>Print Officers</td>
                  </tr>
                  <tr>
                    <td>Finalize</td>
                    <td>
                      Add final signature and seal to a printed certificate
                    </td>
                    <td>Senior Officers</td>
                  </tr>
                  <tr>
                    <td>Users</td>
                    <td>Add, edit, or deactivate system users</td>
                    <td>Administrators</td>
                  </tr>
                  <tr>
                    <td>Roles</td>
                    <td>Define what each group of users can do</td>
                    <td>Administrators</td>
                  </tr>
                  <tr>
                    <td>Workflows</td>
                    <td>Configure the approval process stages</td>
                    <td>Administrators</td>
                  </tr>
                  <tr>
                    <td>Settings</td>
                    <td>Configure system preferences and service charges</td>
                    <td>Administrators</td>
                  </tr>
                  <tr>
                    <td>Master Data</td>
                    <td>Manage lookup lists (counties, currencies, etc.)</td>
                    <td>Administrators</td>
                  </tr>
                  <tr>
                    <td>Audit Logs</td>
                    <td>
                      See a full history of all actions taken in the system
                    </td>
                    <td>Administrators</td>
                  </tr>
                </tbody>
              </table>
            </section>

            {/* ── Login ── */}
            <section id="section-login" className="manual-section">
              <h2 className="manual-h2">🔐 Logging In</h2>
              <p>
                Before you can use any part of the system, you must log in with
                your username and password. Your administrator will give you
                these details when your account is created.
              </p>

              <h3 className="manual-h3">Steps to Log In</h3>
              <ol className="manual-steps">
                <li>
                  Open your web browser (e.g., Google Chrome, Microsoft Edge).
                </li>
                <li>
                  Type the system address in the address bar and press{" "}
                  <kbd>Enter</kbd>.
                </li>
                <li>
                  The login screen will appear. Type your{" "}
                  <strong>Username</strong> in the first box.
                </li>
                <li>
                  Type your <strong>Password</strong> in the second box. The
                  password is hidden — you will see dots instead of letters.
                </li>
                <li>
                  Click the <strong>Login</strong> button.
                </li>
                <li>
                  If your details are correct, you will be taken to the
                  Dashboard.
                </li>
              </ol>

              <div className="manual-warning">
                <strong>⚠️ Important:</strong> Never share your password with
                anyone. If you think someone else knows your password, contact
                your administrator immediately.
              </div>

              <h3 className="manual-h3">Forgot Your Password?</h3>
              <ol className="manual-steps">
                <li>
                  On the login page, click <strong>Forgot Password</strong>.
                </li>
                <li>Enter the email address linked to your account.</li>
                <li>
                  Check your email for a reset link and follow the instructions.
                </li>
              </ol>

              <h3 className="manual-h3">Logging Out</h3>
              <ol className="manual-steps">
                <li>Click your name in the top-right corner of the screen.</li>
                <li>
                  Click <strong>Logout</strong> in the menu that appears.
                </li>
                <li>You will be returned to the login screen.</li>
              </ol>
              <div className="manual-tip">
                <strong>💡 Tip:</strong> Always log out when you leave your desk
                to keep the system secure.
              </div>
            </section>

            {/* ── Dashboard ── */}
            <section id="section-dashboard" className="manual-section">
              <h2 className="manual-h2">🏠 Dashboard</h2>
              <p>
                After logging in, the first screen you see is the{" "}
                <strong>Dashboard</strong>. It gives you a quick summary of
                everything happening in the system.
              </p>

              <h3 className="manual-h3">What You Will See</h3>
              <ul className="manual-list">
                <li>
                  <strong>Summary Cards</strong> — four cards show totals for
                  <strong> Processed Certificates</strong>,
                  <strong> Pending Certificates</strong>,
                  <strong> Revenue</strong>, and <strong>Reprint</strong>, each
                  split by Marriage and Divorce.
                </li>
                <li>
                  <strong>Certificates Processing Trend</strong> — a line chart
                  that compares Marriage and Divorce application volumes over a
                  selected date range.
                </li>
                <li>
                  <strong>Applications Per County</strong> — a county-level bar
                  chart showing Marriage vs Divorce application counts for a
                  selected date range.
                </li>
              </ul>

              <h3 className="manual-h3">Using Dashboard Date Filters</h3>
              <ol className="manual-steps">
                <li>
                  In each chart card, use the <strong>From</strong> and
                  <strong> To</strong> calendar fields to set your reporting
                  period.
                </li>
                <li>
                  The charts refresh automatically after you change either date.
                </li>
                <li>
                  On <strong>Applications Per County</strong>, hover over each
                  bar to see exact values.
                </li>
              </ol>

              <h3 className="manual-h3">Navigating the System</h3>
              <ul className="manual-list">
                <li>
                  The <strong>left sidebar</strong> is the main menu. Click any
                  item to go to that section.
                </li>
                <li>
                  On small screens, click the{" "}
                  <strong>hamburger icon (☰)</strong> at the top-left to open
                  or close the sidebar.
                </li>
                <li>
                  The <strong>bell icon 🔔</strong> at the top-right shows the
                  number of pending tasks assigned to you.
                </li>
                <li>
                  Your <strong>name and photo</strong> appear in the top-right
                  corner. Click them to access your profile or log out.
                </li>
              </ul>
            </section>

            {/* ── Apply ── */}
            <section id="section-apply" className="manual-section">
              <h2 className="manual-h2">
                📝 Applying for a Marriage Certificate
              </h2>
              <p>
                This section is used to submit a new application for a
                traditional marriage certificate on behalf of a couple.
              </p>

              <h3 className="manual-h3">How to Open the Apply Page</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Marriage</strong> to expand the
                  menu.
                </li>
                <li>
                  Click <strong>Apply</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Viewing Existing Applications</h3>
              <p>
                The page shows a table of all applications you have submitted.
                You can:
              </p>
              <ul className="manual-list">
                <li>
                  Use the <strong>Search box</strong> to find an application by
                  form number, email, or category.
                </li>
                <li>
                  Use the <strong>County filter</strong> to show only
                  applications from a specific county.
                </li>
                <li>
                  Use the <strong>Status filter</strong> to show applications at
                  a specific stage (e.g., Created, Submitted, Approved).
                </li>
                <li>
                  Click the <strong>eye icon 👁️</strong> in the Actions column
                  to view the full details of an application.
                </li>
              </ul>

              <div className="manual-tip">
                <strong>💡 Tip:</strong> On the Apply list, the
                <strong> Status</strong> filter defaults to
                <strong> Created</strong> so new records are easy to find first.
              </div>

              <h3 className="manual-h3">Submitting a New Application</h3>
              <ol className="manual-steps">
                <li>
                  Click the <strong>➕ New Application</strong> button in the
                  top-right of the page.
                </li>
                <li>
                  A form will slide in from the right side of the screen. Fill
                  in all the required fields (marked with a red asterisk{" "}
                  <span style={{ color: "red" }}>*</span>).
                </li>
                <li>
                  <strong>Process at Headquarters</strong> — Toggle this switch
                  ON if the application should be processed at the head office
                  instead of a county office.
                </li>
                <li>
                  <strong>County</strong> — Select the county where the marriage
                  took place.
                </li>
                <li>
                  <strong>Place of Marriage</strong> — Type the specific
                  location of the marriage ceremony.
                </li>
                <li>
                  <strong>Date of Marriage</strong> — Click the calendar field
                  and select the marriage date. The date cannot be in the past.
                </li>
                <li>
                  <strong>Email</strong> — Optionally enter the couple's email
                  address for notifications.
                </li>
                <li>
                  The <strong>Currency</strong> and <strong>Charge</strong>{" "}
                  fields are filled in automatically — do not change them.
                </li>
                <li>
                  When all fields are complete, click{" "}
                  <strong>Submit Application</strong>.
                </li>
                <li>
                  A success message will appear and the new application will
                  appear in the table.
                </li>
              </ol>

              <div className="manual-tip">
                <strong>💡 Tip:</strong> After submitting, the application
                status will be
                <strong> Created</strong>. You must then open it and click{" "}
                <strong>Submit</strong>
                inside the detail screen to move it to the approval queue.
              </div>

              <h3 className="manual-h3">Viewing Application Details</h3>
              <ol className="manual-steps">
                <li>
                  Click the <strong>👁️</strong> icon next to any application.
                </li>
                <li>
                  The detail screen opens showing all information about the
                  application.
                </li>
                <li>
                  From here you can add <strong>Applicants</strong> (Groom and
                  Bride details), add an <strong>Officiator</strong>, upload{" "}
                  <strong>Attachments</strong>, manage{" "}
                  <strong>Current Spouse or Spouses</strong>, and record a{" "}
                  <strong>Payment</strong>.
                </li>
                <li>
                  In the <strong>Groom</strong> section, the
                  <strong> Delete Groom</strong> and <strong>Save Groom</strong>
                  buttons appear above the{" "}
                  <strong>Current Spouse or Spouses</strong>
                  accordion.
                </li>
                <li>
                  Open the <strong>Current Spouse or Spouses</strong> accordion
                  to view existing records in a table.
                </li>
                <li>
                  Use <strong>Add New Spouse</strong> to create a record. Use
                  the <strong>✏️</strong> icon in the table to edit an existing
                  spouse and the <strong>🗑️</strong> icon to delete a spouse.
                </li>
                <li>
                  Once all information is complete, click the{" "}
                  <strong>Submit</strong> button to send the application for
                  approval.
                </li>
              </ol>
            </section>

            {/* ── Approve ── */}
            <section id="section-approve" className="manual-section">
              <h2 className="manual-h2">✅ Approving Applications</h2>
              <p>
                The Approval section is used by supervisors and officers who
                have been given approval rights. Applications submitted by
                registry officers will appear here for review.
              </p>

              <h3 className="manual-h3">How to Open the Approval Page</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Marriage</strong>.
                </li>
                <li>
                  Click <strong>Approve</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Reviewing an Application</h3>
              <ol className="manual-steps">
                <li>The table shows all applications waiting for approval.</li>
                <li>
                  Click the <strong>👁️ View</strong> button next to an
                  application to open it.
                </li>
                <li>
                  Read through all the details carefully — Groom information,
                  Bride information, Current Spouse records, Officiator,
                  Attachments, and Payment.
                </li>
                <li>
                  Check the <strong>Workflow Stages</strong> section to see
                  where the application is in the approval process and which
                  stage you are at.
                </li>
              </ol>

              <h3 className="manual-h3">Approving an Application</h3>
              <ol className="manual-steps">
                <li>
                  Once you are satisfied with the application, click the{" "}
                  <strong>Approve</strong> button.
                </li>
                <li>
                  A confirmation message will appear asking{" "}
                  <em>"Are you sure you want to approve this application?"</em>
                </li>
                <li>
                  Click <strong>Yes, approve</strong> to confirm.
                </li>
                <li>
                  The application will move to the next stage in the workflow.
                  An email notification will be sent automatically if email
                  notifications are enabled.
                </li>
              </ol>

              <h3 className="manual-h3">Querying an Application</h3>
              <p>
                If you find a problem with the application and it needs to be
                corrected before approval:
              </p>
              <ol className="manual-steps">
                <li>
                  Click the <strong>Query</strong> button.
                </li>
                <li>
                  A text box will appear. Type the reason for querying (e.g.,
                  "Missing bride's birth certificate").
                </li>
                <li>
                  Click <strong>Submit</strong>. The application will be sent
                  back to the registry officer for correction.
                </li>
              </ol>

              <h3 className="manual-h3">Rejecting an Application</h3>
              <ol className="manual-steps">
                <li>
                  Click <strong>Reject</strong>.
                </li>
                <li>Type the reason for rejection.</li>
                <li>
                  Click <strong>Confirm</strong>. The application will be marked
                  as rejected and the applicant will be notified.
                </li>
              </ol>

              <div className="manual-warning">
                <strong>⚠️ Note:</strong> Rejected applications cannot be
                reversed. Only query an application if it can still be
                corrected.
              </div>
            </section>

            {/* ── Print ── */}
            <section id="section-print" className="manual-section">
              <h2 className="manual-h2">🖨️ Printing Certificates</h2>
              <p>
                Once an application is fully approved through all workflow
                stages, it becomes available for printing. This section is used
                to generate the official marriage certificate document.
              </p>

              <h3 className="manual-h3">How to Open the Print Page</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Marriage</strong>.
                </li>
                <li>
                  Click <strong>Print</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Printing a Certificate</h3>
              <ol className="manual-steps">
                <li>
                  The table shows all applications that are ready to print.
                </li>
                <li>
                  Find the application you want and click{" "}
                  <strong>👁️ View</strong>.
                </li>
                <li>Review the certificate details on screen.</li>
                <li>
                  Click the <strong>Print</strong> button to open the print
                  preview.
                </li>
                <li>
                  In the print dialog that appears, select your printer and
                  click <strong>Print</strong>.
                </li>
                <li>
                  Once printed, the certificate status will update
                  automatically.
                </li>
              </ol>

              <div className="manual-tip">
                <strong>💡 Tip:</strong> Make sure your printer is connected and
                has paper loaded before clicking Print.
              </div>

              <h3 className="manual-h3">Reprint Module</h3>
              <ul className="manual-list">
                <li>
                  <strong>Marriage → Reprint</strong> is available in the menu
                  and currently displays a <strong>Coming Soon</strong> notice.
                </li>
                <li>
                  <strong>Divorce → Reprint</strong> is also currently marked as
                  <strong> Coming Soon</strong>.
                </li>
              </ul>
            </section>

            {/* ── Finalize ── */}
            <section id="section-finalize" className="manual-section">
              <h2 className="manual-h2">📋 Finalizing Certificates</h2>
              <p>
                Finalizing is the last step before a certificate is officially
                complete. It usually involves adding a digital signature and the
                official seal to the printed certificate.
              </p>

              <h3 className="manual-h3">How to Finalize a Certificate</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Marriage → Finalize</strong>.
                </li>
                <li>
                  Find the certificate you want to finalize and click{" "}
                  <strong>👁️ View</strong>.
                </li>
                <li>
                  Review the certificate one last time. All information (groom,
                  bride, current spouse records, officiator, dates) should be
                  correct.
                </li>
                <li>
                  Click <strong>Finalize</strong>. The system will attach the
                  official signature and seal.
                </li>
                <li>
                  Click <strong>Print Signed Certificate</strong> to print the
                  final version for distribution.
                </li>
              </ol>

              <div className="manual-warning">
                <strong>⚠️ Caution:</strong> Once a certificate is finalized,
                the content cannot be changed. Double-check all details before
                finalizing.
              </div>
            </section>

            {/* ── Users ── */}
            <section id="section-users" className="manual-section">
              <h2 className="manual-h2">👥 Managing Users</h2>
              <p>
                The Users section is only available to system administrators. It
                is used to create accounts for new staff members and manage
                existing accounts.
              </p>

              <h3 className="manual-h3">Viewing Users</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Admin → Users</strong>.
                </li>
                <li>
                  A table of all users appears. You can search by name, email,
                  or username.
                </li>
              </ol>

              <h3 className="manual-h3">Creating a New User</h3>
              <ol className="manual-steps">
                <li>
                  Click <strong>➕ New User</strong>.
                </li>
                <li>
                  Fill in the user's <strong>First Name</strong>,{" "}
                  <strong>Last Name</strong>, <strong>Username</strong>, and{" "}
                  <strong>Email</strong>.
                </li>
                <li>
                  Select the user's <strong>Role</strong> (e.g., Registry
                  Officer, Supervisor, Administrator).
                </li>
                <li>
                  Click <strong>Save</strong>. The system will send the new user
                  an email with instructions to set their password.
                </li>
              </ol>

              <h3 className="manual-h3">Editing a User</h3>
              <ol className="manual-steps">
                <li>
                  Click the <strong>✏️ Edit</strong> button next to the user's
                  name.
                </li>
                <li>Change the fields you need to update.</li>
                <li>
                  Click <strong>Save Changes</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Deactivating a User</h3>
              <p>
                If a staff member leaves the organization, you should deactivate
                their account instead of deleting it, so that the history of
                their actions is preserved.
              </p>
              <ol className="manual-steps">
                <li>Open the user's detail page.</li>
                <li>
                  Toggle the <strong>Active</strong> switch to OFF.
                </li>
                <li>
                  Click <strong>Save</strong>. The user will no longer be able
                  to log in.
                </li>
              </ol>
            </section>

            {/* ── Roles ── */}
            <section id="section-roles" className="manual-section">
              <h2 className="manual-h2">🔒 Roles &amp; Permissions</h2>
              <p>
                A <strong>Role</strong> is a named set of permissions. Instead
                of giving permissions to each user individually, you assign a
                role to the user and the role controls what they can see and do.
              </p>
              <p>
                Example roles: <em>Registry Officer</em>, <em>Supervisor</em>,{" "}
                <em>Print Officer</em>, <em>Administrator</em>.
              </p>

              <h3 className="manual-h3">Viewing Roles</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Admin → Roles</strong>.
                </li>
                <li>A table of all roles appears.</li>
              </ol>

              <h3 className="manual-h3">Creating a New Role</h3>
              <ol className="manual-steps">
                <li>
                  Click <strong>➕ New Role</strong>.
                </li>
                <li>
                  Enter the <strong>Role Name</strong> (e.g., "County Officer").
                </li>
                <li>
                  Optionally add a <strong>Description</strong>.
                </li>
                <li>
                  In the <strong>Permissions</strong> section, tick the boxes
                  for each action this role is allowed to perform (View, Create,
                  Edit, Delete, Approve, etc.) for each module.
                </li>
                <li>
                  Click <strong>Save</strong>.
                </li>
              </ol>

              <div className="manual-tip">
                <strong>💡 Tip:</strong> Always follow the{" "}
                <em>principle of least privilege</em> — give users only the
                permissions they need to do their job.
              </div>
            </section>

            {/* ── Workflows ── */}
            <section id="section-workflows" className="manual-section">
              <h2 className="manual-h2">🔄 Workflows</h2>
              <p>
                A <strong>Workflow</strong> defines the sequence of approval
                steps that an application must pass through before it can be
                finalized. Each step (called a<em> Stage</em>) is assigned to a
                specific role.
              </p>
              <p>
                Example workflow:{" "}
                <em>
                  Registry Officer → County Supervisor → Director → Finalization
                </em>
              </p>

              <h3 className="manual-h3">Viewing Workflows</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Admin → Workflows</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Creating a New Workflow</h3>
              <ol className="manual-steps">
                <li>
                  Click <strong>➕ New Workflow</strong>.
                </li>
                <li>
                  Enter a <strong>Workflow Name</strong> (e.g., "County Marriage
                  Workflow").
                </li>
                <li>
                  Select the <strong>Module</strong> this workflow applies to.
                </li>
                <li>
                  Add stages by clicking <strong>Add Stage</strong>. For each
                  stage, select the <strong>Role</strong> responsible and the{" "}
                  <strong>Order</strong> (1 = first stage).
                </li>
                <li>
                  Click <strong>Save</strong> when all stages are added.
                </li>
              </ol>
            </section>

            {/* ── Settings ── */}
            <section id="section-settings" className="manual-section">
              <h2 className="manual-h2">⚙️ Settings</h2>
              <p>
                The Settings page allows administrators to configure how the
                system behaves. It is organized into accordion sections — click
                a section title to expand it.
              </p>

              <h3 className="manual-h3">General Settings</h3>
              <ul className="manual-list">
                <li>
                  <strong>Application Name</strong> — The name displayed in the
                  browser and on documents.
                </li>
                <li>
                  <strong>Ministry Name</strong> — The ministry name printed on
                  certificates.
                </li>
                <li>
                  <strong>Default Currency</strong> — The currency used for
                  service charges.
                </li>
                <li>
                  <strong>Date Format</strong> — How dates are displayed
                  throughout the system.
                </li>
                <li>
                  <strong>Minimum User Age</strong> — The minimum age allowed
                  for applicants.
                </li>
              </ul>

              <h3 className="manual-h3">Email Settings</h3>
              <p>
                Configure the outgoing email server (SMTP) so the system can
                send emails for notifications and password resets.
              </p>
              <ul className="manual-list">
                <li>
                  <strong>SMTP Host</strong> — The address of your email server.
                </li>
                <li>
                  <strong>SMTP Port</strong> — Usually 587 for secure email.
                </li>
                <li>
                  <strong>Username / Password</strong> — The credentials for the
                  email account.
                </li>
                <li>
                  <strong>From Email / From Name</strong> — The sender email and
                  display name.
                </li>
              </ul>

              <h3 className="manual-h3">Notification Settings</h3>
              <ul className="manual-list">
                <li>
                  <strong>Email Notifications</strong> — Turn ON to send email
                  alerts when applications are submitted or approved.
                </li>
                <li>
                  <strong>Workflow Reminders</strong> — Turn ON to send reminder
                  emails for pending approval tasks.
                </li>
              </ul>
              <div className="manual-tip">
                <strong>💡 Note:</strong> Email notifications are only sent when{" "}
                <em>both</em> Email Notifications <em>and</em> Workflow
                Reminders are turned ON.
              </div>

              <h3 className="manual-h3">Security Settings</h3>
              <ul className="manual-list">
                <li>
                  <strong>Session Timeout</strong> — Number of minutes before an
                  inactive user is automatically logged out.
                </li>
                <li>
                  <strong>Password Requirements</strong> — Configure minimum
                  length, special characters, numbers, and uppercase letters.
                </li>
                <li>
                  <strong>Two-Factor Authentication</strong> — Require an extra
                  verification step when logging in.
                </li>
              </ul>

              <h3 className="manual-h3">Service Charges</h3>
              <p>
                Set the fee charged for each type of certificate application.
              </p>
              <ol className="manual-steps">
                <li>
                  Click <strong>+ Add Service Price</strong>.
                </li>
                <li>
                  Select the <strong>Service Category</strong> (e.g., Marriage
                  Certificate).
                </li>
                <li>
                  Enter the <strong>Charge</strong> amount.
                </li>
                <li>
                  Select the <strong>Currency</strong>.
                </li>
                <li>
                  Click <strong>Create</strong>.
                </li>
              </ol>

              <div className="manual-tip">
                <strong>💡 Remember:</strong> After making any changes in
                Settings, click
                <strong> Save Changes</strong> at the bottom of each section.
              </div>
            </section>

            {/* ── Master Data ── */}
            <section id="section-masterdata" className="manual-section">
              <h2 className="manual-h2">🗂️ Master Data</h2>
              <p>
                Master Data contains the reference lists used throughout the
                system — such as counties, currencies, nationalities, and
                document types. Keeping this data accurate ensures the rest of
                the system works correctly.
              </p>

              <h3 className="manual-h3">Viewing Master Data</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Admin → Master Data</strong>.
                </li>
                <li>
                  Use the <strong>Category filter</strong> to narrow down the
                  list (e.g., select "County" to see all counties).
                </li>
              </ol>

              <h3 className="manual-h3">Adding a New Entry</h3>
              <ol className="manual-steps">
                <li>
                  Click <strong>➕ New Entry</strong>.
                </li>
                <li>
                  Select the <strong>Category</strong>, enter the{" "}
                  <strong>Name</strong> and any other required fields.
                </li>
                <li>
                  Click <strong>Save</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Editing or Deactivating an Entry</h3>
              <ol className="manual-steps">
                <li>
                  Click the <strong>✏️ Edit</strong> button next to the entry.
                </li>
                <li>
                  Make your changes, or toggle the <strong>Active</strong>{" "}
                  switch to deactivate the entry so it no longer appears in
                  dropdown menus.
                </li>
                <li>
                  Click <strong>Save</strong>.
                </li>
              </ol>
            </section>

            {/* ── Audit Logs ── */}
            <section id="section-auditlogs" className="manual-section">
              <h2 className="manual-h2">📊 Audit Logs</h2>
              <p>
                The Audit Logs section records every significant action
                performed in the system — who did what, what was changed, and
                when. This is important for accountability and security.
              </p>

              <h3 className="manual-h3">Viewing Audit Logs</h3>
              <ol className="manual-steps">
                <li>
                  In the sidebar, click <strong>Admin → Audit Logs</strong>.
                </li>
                <li>
                  A table shows all recent actions with columns for{" "}
                  <strong>Date</strong>, <strong>User</strong>,{" "}
                  <strong>Action</strong>, and <strong>Module</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Filtering Logs</h3>
              <ul className="manual-list">
                <li>
                  Use the <strong>Search box</strong> to find actions related to
                  a specific user or record.
                </li>
                <li>
                  Use the <strong>Date range filter</strong> to view logs from a
                  specific period.
                </li>
                <li>
                  Use the <strong>Module filter</strong> to see actions from
                  only one part of the system.
                </li>
              </ul>

              <div className="manual-tip">
                <strong>💡 Tip:</strong> Audit logs cannot be deleted or edited.
                They are a permanent record of all activity in the system.
              </div>
            </section>

            {/* ── Profile ── */}
            <section id="section-profile" className="manual-section">
              <h2 className="manual-h2">👤 Your Profile</h2>
              <p>
                The Profile page lets you update your personal information and
                change your password.
              </p>

              <h3 className="manual-h3">Opening Your Profile</h3>
              <ol className="manual-steps">
                <li>
                  Click your <strong>name or photo</strong> in the top-right
                  corner.
                </li>
                <li>
                  Click <strong>Profile</strong> in the dropdown menu.
                </li>
              </ol>

              <h3 className="manual-h3">Updating Your Information</h3>
              <ol className="manual-steps">
                <li>
                  Edit any of the fields on your profile page (name, email,
                  phone number).
                </li>
                <li>
                  To upload a profile photo, click <strong>Upload Photo</strong>{" "}
                  and select an image from your computer.
                </li>
                <li>
                  Click <strong>Save Changes</strong>.
                </li>
              </ol>

              <h3 className="manual-h3">Changing Your Password</h3>
              <ol className="manual-steps">
                <li>
                  Scroll to the <strong>Change Password</strong> section.
                </li>
                <li>
                  Enter your <strong>Current Password</strong>.
                </li>
                <li>
                  Enter your <strong>New Password</strong>. It must meet the
                  password requirements set by your administrator.
                </li>
                <li>
                  Re-enter the new password in the{" "}
                  <strong>Confirm Password</strong> field.
                </li>
                <li>
                  Click <strong>Change Password</strong>.
                </li>
              </ol>

              <div className="manual-warning">
                <strong>⚠️ Security Reminder:</strong> Use a strong password
                that is at least 8 characters long and includes a mix of
                letters, numbers, and symbols. Do not reuse old passwords.
              </div>
            </section>

            {/* ── End ── */}
            <section className="manual-section manual-end">
              <p>
                For technical support or to report a problem, please contact
                your system administrator.
              </p>
              <p style={{ color: "#8a9ab5", fontSize: "13px" }}>
                TWDC Users' Manual — Ministry of Internal Affairs, Liberia
              </p>
            </section>
          </article>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default UsersManual;
